import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, LinkedinLogo, Certificate } from "@phosphor-icons/react";
import Header from "@/components/Header";
import { Reveal } from "@/components/Reveal";
import { CALENDLY_URL, BOARDROOM_BG } from "@/constants/site";

const EASE = [0.16, 1, 0.3, 1];

const HERO_LINES = [
  { text: "30+ Years of", accent: false },
  { text: "Enterprise Software", accent: false },
  { text: "Gravity—On Demand.", accent: true },
];

const STAGE_PHOTOS = [
  {
    src: "/stage/pass-speaker.png",
    tag: "PASS Data Community Summit · 2025",
    caption: "Speaker badge in hand, Seattle",
    testId: "stage-photo-pass",
  },
  {
    src: "/stage/ai-ready-talk.png",
    tag: "Conference Session",
    caption: "\u201cAI-Ready Data Architecture: Navigating Trade-offs for Real-World Success\u201d",
    testId: "stage-photo-ai-ready",
  },
  {
    src: "/stage/session-podium.jpg",
    tag: "Conference Session",
    caption: "Data strategy session with practitioners",
    testId: "stage-photo-podium",
  },
];

const StageFigure = ({ photo, tall = false }) => (
  <figure data-testid={photo.testId} className="group">
    <div className="relative border border-white/10 overflow-hidden">
      <img
        src={photo.src}
        alt={photo.caption}
        className={`w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03] ${
          tall ? "aspect-[3/5]" : "aspect-[4/3]"
        }`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/75 via-transparent to-transparent pointer-events-none" />
      <figcaption className="absolute bottom-0 inset-x-0 p-5">
        <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9]">
          {photo.tag}
        </span>
        <span className="block text-sm font-light text-white/85 mt-1.5">
          {photo.caption}
        </span>
      </figcaption>
    </div>
  </figure>
);

const MEDIA = [
  {
    kind: "Panel Webinar",
    publisher: "Database Trends & Applications",
    title: "How AI is Augmenting, Not Automating, the DBA",
    desc: "Expert perspective on the skills, tools, and mindsets database professionals need in the AI era \u2014 and why human judgment, business context, and strategy remain irreplaceable.",
    quote: "AI won\u2019t replace DBAs, but DBAs who leverage AI might replace those who don\u2019t.",
    url: "https://www.dbta.com/Editorial/News-Flashes/How-AI-is-Augmenting-Not-Automating-the-DBA-with-Quest-and-Datavail-170916.aspx",
    testId: "media-dba-ai",
  },
  {
    kind: "Analyst Webcast",
    publisher: "BrightTALK · ESG",
    title: "Database Monitoring & Optimization — ESG Technical Validation",
    desc: "A deep dive with analyst firm ESG into the essential functions of enterprise database monitoring across hybrid and multi-cloud estates \u2014 continuous application monitoring, performance tuning, and cloud cost optimization.",
    quote: "",
    url: "https://www.brighttalk.com/webcast/19649/563134",
    testId: "media-brighttalk-esg",
  },
  {
    kind: "Panel Webinar",
    publisher: "Database Trends & Applications",
    title: "The Truth of APM and DB Monitoring",
    desc: "Why database observability is the answer to APM complexity \u2014 and why modern enterprises need both layers working together, not either/or.",
    quote: "",
    url: "https://www.dbta.com/Editorial/News-Flashes/The-Truth-of-APM-and-DB-Monitoring-with-Quest-Software-165682.aspx",
    testId: "media-apm-monitoring",
  },
  {
    kind: "Authored Article",
    publisher: "Quest Blog",
    title: "The ROI of Proactive Database Observability",
    desc: "Four ways observability transforms the business: compliance risk reduction, IT-to-strategy alignment, always-on revenue protection, and cost optimization without sacrificing reliability.",
    quote: "",
    url: "https://blog.quest.com/the-roi-of-proactive-database-observability-four-ways-to-transform-your-business/",
    testId: "media-observability-roi",
  },
  {
    kind: "Podcast \u2014 Co-Host",
    publisher: "Rabbit, Rabbit! Podcast",
    title: "Rabbit, Rabbit! \u2014 Database Performance & Optimization",
    desc: "A monthly podcast co-hosted by Amit covering the world of database performance and optimization \u2014 from DataOps and AI to replication strategy and data democratization.",
    quote: "",
    url: "https://www.youtube.com/channel/UCcJdc4krUi2lU3KbKTPge3A",
    testId: "media-podcast",
  },
];

const LEADERSHIP = [
  {
    number: "01",
    title: "30+ Years of Enterprise Scale",
    body: "Founded by a career Field CTO and database management strategist from the enterprise software industry who helped scale hybrid-SaaS portfolios exceeding $100M ARR.",
    tags: [],
    testId: "leadership-row-scale",
  },
  {
    number: "02",
    title: "Deep Architecture Authority",
    body: "Extensive domain expertise across enterprise data architectures, database observability, Change Data Capture (CDC), Medallion Lakehouses, OpenTelemetry, and sovereign AI infrastructure.",
    tags: ["CDC", "Medallion Lakehouse", "OpenTelemetry", "Sovereign AI", "Data Observability"],
    testId: "leadership-row-architecture",
  },
  {
    number: "03",
    title: "Proven Deal Unblocking",
    body: "Deep mastery of value-selling methodologies to translate complex technical capabilities into board-level strategic value for conservative buyers across Financial Services, Energy, Federal/DoD, and Healthcare.",
    tags: ["Financial Services", "Energy", "Federal / DoD", "Healthcare"],
    testId: "leadership-row-deals",
  },
  {
    number: "04",
    title: "Global Conference Speaker & Keynote",
    body: "Speaker and keynote presenter at the data industry's leading conferences worldwide — sharing field-tested expertise on database performance, observability, and enterprise data strategy with global practitioner audiences.",
    tags: ["PASS Summit", "PostgresConf", "Oracle Develop", "SQL Saturday"],
    testId: "leadership-row-speaking",
  },
];

const PILLARS = [
  {
    title: "The Builder\u2019s DNA",
    body: "Led the 0-to-1 launch and market expansion of premier cloud and SaaS data platform offerings, scaling a combined portfolio to over $50M in recurring revenue.",
    testId: "pillar-card-builders-dna",
  },
  {
    title: "Hands-On Technical Depth",
    body: "I don\u2019t just talk about generative AI; I actively prototype agentic AI integrations and custom telemetry tools myself. That depth is what lets me hold the room in tough security, latency, and compliance discussions — proof in the room, not slideware.",
    testId: "pillar-card-ai-velocity",
  },
  {
    title: "Consensus-Driven Execution",
    body: "Enterprise deals are won by aligning diverse stakeholders\u2014from developers to procurement. I excel at quarterbacking internal cross-functional squads to deliver frictionless, high-trust customer journeys that protect and grow ARR.",
    testId: "pillar-card-consensus",
  },
];

const PRINCIPLES = [
  {
    number: "01",
    title: "Counsel Over Coverage",
    body: "I do not deliver passive decks or generic advice. I practice as scarce Field CTO counsel — architecting the system, qualifying the pursuit, and sitting the rooms that close. Your team executes the motion.",
    testId: "principle-card-execution",
  },
  {
    number: "02",
    title: "Capital Efficiency",
    body: "Eliminating the $400k+ fixed annual drag of full-time presales leadership by offering agile, sprint-based executive capability.",
    testId: "principle-card-capital",
  },
  {
    number: "03",
    title: "Technical Integrity",
    body: "Every strategy is grounded in real-world database performance, modern data governance, and scalable enterprise architecture.",
    testId: "principle-card-integrity",
  },
];

export default function WhoWeArePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="about-page" className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <section
          ref={heroRef}
          data-testid="about-hero"
          className="relative min-h-[85vh] flex flex-col justify-end overflow-hidden"
        >
          <motion.div style={{ y: bgY }} className="absolute inset-0">
            <img
              src={BOARDROOM_BG}
              alt=""
              className="h-[125%] w-full object-cover object-center opacity-50"
            />
            <div className="absolute inset-0 bg-black/75" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-[#050505]/70" />
          </motion.div>

          <div className="relative z-10 max-w-[1440px] mx-auto w-full px-6 lg:px-12 pt-40 pb-16 lg:pb-24">
            <motion.p
              initial={reduced ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-8 lg:mb-12"
            >
              Who I Am — Tech Executive Capability Center LLC
            </motion.p>

            <h1
              data-testid="about-hero-headline"
              className="font-serif tracking-[-0.03em] leading-[1.05] text-5xl sm:text-7xl lg:text-8xl text-white"
            >
              {HERO_LINES.map((line, i) => (
                <span key={line.text} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${line.accent ? "italic text-[#E6E4D9]" : ""}`}
                    initial={reduced ? false : { y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1.2, ease: EASE, delay: 0.25 + i * 0.14 }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              data-testid="about-hero-subheadline"
              initial={reduced ? false : { opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 0.9 }}
              className="mt-8 lg:mt-10 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-white/70"
            >
              Bridging the gap between complex engineering architectures and
              high-velocity enterprise revenue.
            </motion.p>
          </div>
        </section>

        <section className="px-6 lg:px-12 py-20 lg:py-28 border-b border-white/10">
          <div className="max-w-[1440px] mx-auto">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E6E4D9] mb-8">
                Core Positioning
              </p>
              <p
                data-testid="about-positioning"
                className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white/85 leading-[1.4] max-w-5xl"
              >
                TECC LLC was founded to solve a structural gap in
                early-to-mid-stage B2B software companies: high-potential tech
                platforms frequently lose high-stakes Global 2000 deals{" "}
                <span className="italic text-[#E6E4D9]">
                  not because their technology lacks power
                </span>
                , but because they lack field executive presence, C-suite
                validation, and tailored messaging for conservative enterprise
                buyers.
              </p>
            </Reveal>
          </div>
        </section>

        <section
          data-testid="founder-section"
          className="px-6 lg:px-12 py-24 lg:py-32 border-b border-white/10"
        >
          <div className="max-w-[1440px] mx-auto">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
                01 — The Founder
              </p>
              <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.1]">
                A Builder Who{" "}
                <span className="italic text-[#E6E4D9]">Loves to Sell</span>
              </h2>
            </Reveal>

            <div className="mt-14 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
              <Reveal className="lg:col-span-5">
                <figure>
                  <div className="relative border border-white/10 overflow-hidden">
                    <img
                      src="/founder.png"
                      alt="Founder and Field CTO of TECC LLC"
                      data-testid="founder-photo"
                      className="w-full h-auto object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/50 via-transparent to-transparent pointer-events-none" />
                  </div>
                  <figcaption className="mt-4 flex items-center justify-between gap-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                      Amit Parikh — Founder &amp; Field CTO
                    </span>
                    <a
                      href="https://www.linkedin.com/in/amit-parikh-50b968/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="founder-linkedin-link"
                      className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-[#E6E4D9] transition-colors duration-300"
                    >
                      <LinkedinLogo size={14} />
                      LinkedIn
                    </a>
                  </figcaption>
                  <a
                    href="https://learn.microsoft.com/api/credentials/share/en-us/AmitParikh-0416/9325EF2A244A81AA?sharingId"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="founder-credential-link"
                    className="group mt-6 flex items-center gap-4 border border-white/10 px-5 py-4 hover:border-[#E6E4D9]/40 hover:bg-white/[0.02] transition-colors duration-300"
                  >
                    <Certificate
                      size={26}
                      weight="light"
                      className="text-[#E6E4D9] shrink-0"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                        Microsoft Certified · Issued Mar 2025
                      </span>
                      <span className="block text-sm text-white/85 mt-1.5">
                        Azure AI Fundamentals
                      </span>
                      <span className="block font-mono text-[10px] tracking-[0.15em] text-white/30 mt-1">
                        Credential ID 9325EF2A244A81AA
                      </span>
                    </span>
                    <ArrowUpRight
                      size={16}
                      weight="light"
                      className="text-white/30 shrink-0 transition-all duration-300 group-hover:text-[#E6E4D9] group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </a>
                </figure>
              </Reveal>

              <div className="lg:col-span-7">
                <Reveal delay={0.1}>
                  <p className="text-base sm:text-lg font-light leading-relaxed text-white/70 mb-7">
                    I have spent nearly three decades in enterprise
                    technology—most recently as Field CTO for a global
                    enterprise software leader—living at the intersection of
                    high-scale engineering and board-level business strategy.
                  </p>
                  <blockquote className="border-l-2 border-[#E6E4D9] pl-6 lg:pl-8 my-10 font-serif italic text-xl sm:text-2xl text-[#E6E4D9] leading-relaxed">
                    At my core, I am a founder and a builder who loves to sell.
                  </blockquote>
                  <p className="text-base sm:text-lg font-light leading-relaxed text-white/70 mb-7">
                    Over the years, I realized that the biggest gap in tech
                    adoption isn&rsquo;t the software itself—it&rsquo;s
                    translation. Enterprise buyers are exhausted by superficial
                    pitches. They don&rsquo;t want to be &ldquo;sold to&rdquo;
                    by a generalist; they want a partner who can sit with their
                    CIO, CTO, or CISO, deeply understand their operational
                    bottlenecks, and map complex architectures directly to
                    business outcomes.
                  </p>
                  <p className="text-base sm:text-lg font-light leading-relaxed text-white/70 mb-7">
                    Because I have built and launched products from scratch, I
                    approach enterprise sales with a founder&rsquo;s mindset of
                    extreme ownership, resourcefulness, and diagnostic
                    curiosity.
                  </p>
                  <p className="text-base sm:text-lg font-light leading-relaxed text-white/70">
                    Today, I partner with forward-thinking organizations as an
                    independent advisor and commercial driver, helping them
                    deploy scalable AI strategies, modernize data platforms, and
                    design GTM plans that turn complex technology into
                    measurable business value.
                  </p>
                </Reveal>
              </div>
            </div>

            <Reveal delay={0.1}>
              <p className="mt-16 lg:mt-20 font-mono text-xs uppercase tracking-[0.3em] text-white/50">
                Both Sides of the Table
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {PILLARS.map((pillar, i) => (
                <Reveal key={pillar.title} delay={i * 0.12} className="bg-[#0A0A0A]">
                  <article
                    data-testid={pillar.testId}
                    className="h-full p-10 lg:p-12 hover:bg-[#101010] transition-colors duration-500"
                  >
                    <span className="block h-px w-10 bg-[#E6E4D9]/60 mb-8" />
                    <h3 className="font-serif text-2xl text-white tracking-tight mb-5">
                      {pillar.title}
                    </h3>
                    <p className="text-base font-light leading-relaxed text-white/70">
                      {pillar.body}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <div
                data-testid="founder-off-field"
                className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8"
              >
                <div className="lg:col-span-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9]">
                    Off the Field
                  </p>
                </div>
                <div className="lg:col-span-9">
                  <p className="font-serif italic text-xl sm:text-2xl text-white/80 leading-relaxed max-w-3xl">
                    I am an ICC Certified Level 1 Coach and Match Official.
                    Whether mentoring players on the pitch or helping an
                    executive team navigate a high-stakes deal, the principles
                    are identical: keep a cool head, make split-second decisions
                    under pressure, and build a cohesive team that plays to win.
                  </p>
                  <p className="mt-10 text-base sm:text-lg font-light leading-relaxed text-white/70 max-w-3xl">
                    <a
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="founder-connect-link"
                      className="text-[#E6E4D9] underline underline-offset-8 decoration-white/30 hover:decoration-[#E6E4D9] transition-colors duration-300"
                    >
                      Let&rsquo;s connect
                    </a>
                    —whether you want to discuss scaling generative AI in the
                    enterprise, modern data architectures, or the nuances of a
                    perfect cricket delivery.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section
          data-testid="leadership-section"
          className="px-6 lg:px-12 py-24 lg:py-32"
        >
          <div className="max-w-[1440px] mx-auto">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
                02 — Leadership &amp; Heritage
              </p>
              <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.1]">
                Founder &amp;{" "}
                <span className="italic text-[#E6E4D9]">Field CTO</span>{" "}
                Leadership
              </h2>
            </Reveal>

            <div className="mt-14 lg:mt-20 border-y border-white/10 divide-y divide-white/10">
              {LEADERSHIP.map((item, i) => (
                <Reveal key={item.number} delay={i * 0.08} y={24}>
                  <article
                    data-testid={item.testId}
                    className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 py-12 lg:py-14 px-2 lg:px-4 hover:bg-white/[0.02] transition-colors duration-500"
                  >
                    <div className="lg:col-span-4">
                      <span className="font-mono text-xs text-[#E6E4D9] tracking-[0.3em] block mb-4">
                        {item.number}
                      </span>
                      <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight leading-snug">
                        {item.title}
                      </h3>
                    </div>
                    <div className="lg:col-span-8">
                      <p className="text-base sm:text-lg font-light leading-relaxed text-white/70 max-w-3xl">
                        {item.body}
                      </p>
                      {item.tags.length > 0 && (
                        <div className="mt-6 flex flex-wrap gap-2">
                          {item.tags.map((tag) => (
                            <span
                              key={tag}
                              className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 border border-white/15 px-3 py-1.5"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section
          data-testid="on-stage-section"
          className="px-6 lg:px-12 py-24 lg:py-32 border-t border-white/10"
        >
          <div className="max-w-[1440px] mx-auto">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
                03 — On Stage Worldwide
              </p>
              <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.1]">
                From the{" "}
                <span className="italic text-[#E6E4D9]">Conference Circuit</span>
              </h2>
            </Reveal>

            <div className="mt-14 lg:mt-20 grid grid-cols-1 lg:grid-cols-5 gap-6">
              <Reveal className="lg:col-span-3" y={24}>
                <StageFigure photo={STAGE_PHOTOS[0]} tall />
              </Reveal>
              <div className="lg:col-span-2 grid grid-cols-1 gap-6">
                {STAGE_PHOTOS.slice(1).map((photo, i) => (
                  <Reveal key={photo.src} delay={(i + 1) * 0.08} y={24}>
                    <StageFigure photo={photo} />
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          data-testid="media-section"
          className="px-6 lg:px-12 py-24 lg:py-32 border-t border-white/10"
        >
          <div className="max-w-[1440px] mx-auto">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
                04 — Featured Media
              </p>
              <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.1]">
                In the Press &amp;{" "}
                <span className="italic text-[#E6E4D9]">On Panels</span>
              </h2>
            </Reveal>

            <div className="mt-14 lg:mt-20 border-y border-white/10 divide-y divide-white/10">
              {MEDIA.map((item, i) => (
                <Reveal key={item.testId} delay={i * 0.08} y={24}>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={item.testId}
                    className="group grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-10 py-10 lg:py-12 px-2 lg:px-4 hover:bg-white/[0.02] transition-colors duration-500"
                  >
                    <div className="lg:col-span-3 flex lg:flex-col gap-3 items-start">
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9]">
                        {item.kind}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                        {item.publisher}
                      </span>
                    </div>
                    <div className="lg:col-span-8">
                      <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight leading-snug mb-4 group-hover:text-[#E6E4D9] transition-colors duration-300">
                        {item.title}
                      </h3>
                      <p className="text-base font-light leading-relaxed text-white/60 max-w-3xl">
                        {item.desc}
                      </p>
                      {item.quote && (
                        <p className="mt-5 font-serif italic text-lg text-[#E6E4D9]/80 leading-relaxed">
                          &ldquo;{item.quote}&rdquo;
                        </p>
                      )}
                    </div>
                    <div className="hidden lg:flex lg:col-span-1 items-start justify-end">
                      <ArrowUpRight
                        size={28}
                        weight="light"
                        className="text-white/25 transition-all duration-500 group-hover:text-[#E6E4D9] group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section
          data-testid="principles-section"
          className="px-6 lg:px-12 py-24 lg:py-32"
        >
          <div className="max-w-[1440px] mx-auto">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
                05 — Core Operating Principles
              </p>
              <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.1]">
                How I <span className="italic text-[#E6E4D9]">Operate</span>
              </h2>
            </Reveal>

            <div className="mt-14 lg:mt-20 grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {PRINCIPLES.map((principle, i) => (
                <Reveal
                  key={principle.number}
                  delay={i * 0.12}
                  className="bg-[#0A0A0A]"
                >
                  <article
                    data-testid={principle.testId}
                    className="group relative h-full overflow-hidden p-10 lg:p-12 hover:bg-[#101010] transition-colors duration-500"
                  >
                    <span className="absolute top-6 right-8 font-serif text-7xl lg:text-8xl text-white/5 select-none leading-none">
                      {principle.number}
                    </span>
                    <div className="relative">
                      <span className="block h-px w-10 bg-[#E6E4D9]/60 mb-8" />
                      <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight mb-5">
                        {principle.title}
                      </h3>
                      <p className="text-base font-light leading-relaxed text-white/70">
                        {principle.body}
                      </p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <div className="mt-20 lg:mt-28 border border-white/10 p-10 lg:p-16 flex flex-col lg:flex-row lg:items-center gap-10 justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">
                    Engage TECC LLC
                  </p>
                  <h3 className="font-serif text-3xl sm:text-4xl text-white tracking-tight leading-snug max-w-xl">
                    If the motion is stuck in{" "}
                    <span className="italic text-[#E6E4D9]">
                      the rooms that matter.
                    </span>
                  </h3>
                </div>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-cta-button"
                  className="group shrink-0 inline-flex items-center gap-3 bg-[#E6E4D9] text-black font-mono text-xs sm:text-sm uppercase tracking-[0.15em] px-8 py-4 hover:bg-[#C2C0B6] transition-colors duration-300"
                >
                  Book a Strategy Call
                  <ArrowUpRight
                    size={16}
                    weight="bold"
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 px-6 lg:px-12 py-8">
        <div className="max-w-[1440px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              © 2026 Tech Executive Capability Center LLC
            </p>
            <a
              href="/privacy"
              data-testid="footer-privacy-link"
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white transition-colors duration-300"
            >
              Privacy Policy
            </a>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
            Enterprise GTM &amp; Technical Acceleration
          </p>
        </div>
      </footer>
    </div>
  );
}
